import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  clearToken,
  getToken,
  setSessionNotice,
  setToken,
} from '@crestrewards/api-client';

import * as authApi from '../../features/auth/authApi';

/**
 * Session restore gets a shorter leash than a normal request. The user is
 * staring at a skeleton the whole time, and a 20-second wait is indistinguishable
 * from a hang — better to give up and show them the login form.
 */
const RESTORE_TIMEOUT_MS = 8000;

/**
 * The one place session state lives.
 *
 * `status` is a small state machine rather than a pair of booleans, so a
 * consumer can never be "not loading and not authenticated and not anonymous":
 *
 *   'restoring'      — a token was found, GET /auth/me is in flight
 *   'authenticated'  — token and user are both known good
 *   'anonymous'      — no valid session
 *
 * Note: this is the one sanctioned shared/ -> features/ import. Auth is
 * cross-cutting, and its HTTP calls still live in features/auth/authApi.js so
 * convention 3 holds.
 */
const AuthContext = createContext(null);

/**
 * Works out who the current session belongs to, and reports the answer exactly
 * once through `onSettled`.
 *
 * This is the whole restore policy, deliberately outside the component so the
 * "always settles" guarantee can be exercised without a React runtime.
 *
 * **It never rejects and never settles with anything but a user object or
 * null.** Every outcome — no token, network failure, HTTP error, empty body,
 * unparseable shape, a thrown `onSettled` — funnels through the `finally`.
 *
 * @param {(user: object | null) => void} onSettled
 * @returns {Promise<void>} resolves once `onSettled` has run
 */
export async function startSessionRestore(onSettled) {
  let restoredUser = null;

  try {
    // No token is not a failure — it is simply being signed out.
    if (!getToken()) return;

    const data = await authApi.getCurrentUser({ timeout: RESTORE_TIMEOUT_MS });

    // A response that carries no usable account is a failed restore, not a
    // session. Being unable to confirm who this is means there isn't one.
    const candidate = data?.user;
    if (!candidate || typeof candidate !== 'object') {
      throw new Error('Could not confirm your session. Sign in again.');
    }

    restoredUser = candidate;
  } catch (error) {
    // Expired, revoked, deleted, unreachable, malformed — same outcome.
    clearToken();
    restoredUser = null;
    if (error?.message) setSessionNotice(error.message);
  } finally {
    onSettled(restoredUser);
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [status, setStatus] = useState(() =>
    getToken() ? 'restoring' : 'anonymous',
  );

  // StrictMode mounts effects twice in development; only fetch once.
  const restoreStartedRef = useRef(false);

  useEffect(() => {
    if (restoreStartedRef.current) return;
    restoreStartedRef.current = true;

    // Deliberately no `cancelled` flag and no cleanup that suppresses the
    // transition. An earlier version had both, and StrictMode's
    // mount -> cleanup -> mount cycle stranded the app in 'restoring' forever:
    // the cleanup cancelled the first run's callbacks, then the second run
    // bailed on the ref and set nothing. Leaving 'restoring' must not depend on
    // the component still being mounted. React 18 treats a state update on an
    // unmounted component as a no-op, so there is nothing to guard against.
    startSessionRestore((restoredUser) => {
      setUser(restoredUser);
      setStatus(restoredUser ? 'authenticated' : 'anonymous');
    });
  }, []);

  /**
   * Signs in and stores the token. Rejects with the ApiError from api-client so
   * the caller can render `error.message` inline.
   */
  const login = useCallback(async ({ email, password }) => {
    const data = await authApi.login({ email, password });
    setToken(data.token);
    setUser(data.user);
    setStatus('authenticated');
    return data.user;
  }, []);

  /** Always ends the local session, even if the API call fails. */
  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } catch {
      // Stateless JWT — dropping the token below is what matters.
    } finally {
      clearToken();
      setUser(null);
      setStatus('anonymous');
    }
  }, []);

  const value = useMemo(
    () => ({
      user,
      status,
      isRestoring: status === 'restoring',
      isAuthenticated: status === 'authenticated',
      login,
      logout,
    }),
    [user, status, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used inside an <AuthProvider>.');
  }
  return context;
}

export default useAuth;
